<div [formGroup]="evaluationForm">
    <!-- Evaluation Type Toggle -->
    <div class="row mb-4">
        <div class="col-12 col-md-6">
            <div class="mb-3">
                <label class="form-label fw-bold">Evaluation Type</label>
                <div class="d-flex gap-3">
                    <div class="form-check">
                        <input 
                            class="form-check-input" 
                            type="radio" 
                            name="evaluationTypeRadio" 
                            id="evaluationTypeSeparate" 
                            value="Separate"
                            [checked]="evaluationForm.get('evaluationType')?.value === 'Separate'"
                            (change)="onEvaluationTypeChange('Separate')"
                            [disabled]="readonly && !isPRSelected">
                        <label class="form-check-label" for="evaluationTypeSeparate">Separate evaluation</label>
                    </div>
                    <div class="form-check">
                        <input 
                            class="form-check-input" 
                            type="radio" 
                            name="evaluationTypeRadio" 
                            id="evaluationTypeJoint" 
                            value="Joint"
                            [checked]="evaluationForm.get('evaluationType')?.value === 'Joint'"
                            (change)="onEvaluationTypeChange('Joint')"
                            [disabled]="readonly && !isPRSelected">
                        <label class="form-check-label" for="evaluationTypeJoint">Joint evaluation</label>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Weightage Inputs -->
    <div class="row mb-4">
        <div class="col-12 col-md-4">
            <div class="mb-3">
                <label for="technicalWeightage" class="form-label fw-bold">Technical Weightage</label>
                <input 
                    type="number" 
                    id="technicalWeightage" 
                    class="form-control rounded-4 p-2" 
                    formControlName="technicalWeightage" 
                    [placeholder]="isJointEvaluation ? '50.00' : '100.00'"
                    step="0.01"
                    [min]="isJointEvaluation ? 0 : 100"
                    [max]="isJointEvaluation ? 100 : 100"
                    [style.background-color]="(isSeparateEvaluation && !isPRSelected) || readonly ? '#e9ecef' : ''">
                <small class="text-muted" *ngIf="isJointEvaluation">
                    Adjusting this will automatically update Commercial Weightage to maintain total of 100%
                </small>
                <small class="text-muted" *ngIf="isSeparateEvaluation">
                    Fixed at 100% for separate evaluation
                </small>
            </div>
        </div>
        
        <div class="col-12 col-md-4">
            <div class="mb-3">
                <label for="commercialWeightage" class="form-label fw-bold">Commercial Weightage</label>
                <input 
                    type="number" 
                    id="commercialWeightage" 
                    class="form-control rounded-4 p-2" 
                    formControlName="commercialWeightage" 
                    [placeholder]="isJointEvaluation ? '50.00' : '100.00'"
                    step="0.01"
                    [min]="isJointEvaluation ? 0 : 100"
                    [max]="isJointEvaluation ? 100 : 100"
                    [style.background-color]="(isSeparateEvaluation && !isPRSelected) || readonly ? '#e9ecef' : ''">
                <small class="text-muted" *ngIf="isJointEvaluation">
                    Adjusting this will automatically update Technical Weightage to maintain total of 100%
                </small>
                <small class="text-muted" *ngIf="isSeparateEvaluation">
                    Fixed at 100% for separate evaluation
                </small>
            </div>
        </div>
        
        <div class="col-12 col-md-4">
            <div class="mb-3">
                <label class="form-label fw-bold">Total Weightage</label>
                <input 
                    type="text" 
                    class="form-control rounded-4 p-2 bg-light" 
                    [value]="totalWeightage | number:'1.2-2'" 
                    readonly>
                <small class="text-muted" *ngIf="isJointEvaluation">
                    <span [class.text-success]="totalWeightage === 100" [class.text-warning]="totalWeightage !== 100">
                        {{ totalWeightage === 100 ? '✓ Total equals 100%' : '⚠ Total must equal 100%' }}
                    </span>
                </small>
                <small class="text-muted" *ngIf="isSeparateEvaluation">
                    Total: 200% (Technical: 100% + Commercial: 100%)
                </small>
            </div>
        </div>
    </div>

    <!-- Tabs for Technical/Commercial - Show in both edit and readonly mode -->
    <div class="mb-3">
        <ul class="nav nav-tabs evaluation-tabs">
            <li class="nav-item">
                <a 
                    class="nav-link" 
                    [class.active]="activeEvaluationTab === 'Technical'"
                    (click)="switchEvaluationTab('Technical')"
                    style="cursor: pointer;">
                    Technical
                </a>
            </li>
            <li class="nav-item">
                <a 
                    class="nav-link" 
                    [class.active]="activeEvaluationTab === 'Commercial'"
                    (click)="switchEvaluationTab('Commercial')"
                    style="cursor: pointer;">
                    Commercial
                </a>
            </li>
        </ul>
    </div>

    <!-- Action Buttons -->
    <div class="d-flex gap-2 mb-3" *ngIf="!readonly">
        <button 
            type="button" 
            class="btn action-btn" 
            (click)="addEvaluationCriteria()">
            + New
        </button>
    </div>

    <div class="alert mb-3"
         [class.alert-warning]="isJointEvaluation"
         [class.alert-danger]="isSeparateEvaluation"
         *ngIf="!readonly && getCurrentCriteriaWeightageError()">
        <i class="fas fa-exclamation-triangle me-2"></i>
        {{ getCurrentCriteriaWeightageError() }}
    </div>

    <!-- Total Weightage Summary - Show in both modes -->
    <div class="mb-3">
        <strong>Total {{ activeEvaluationTab }} Criteria Weightage: 
            <span [class.text-success]="!readonly && isCurrentCriteriaValid()" 
                  [class.text-warning]="!readonly && isCurrentCriteriaWarning()"
                  [class.text-danger]="!readonly && isCurrentCriteriaError()">
                {{ getTotalCriteriaWeightage() | number:'1.2-2' }}%
            </span>
        </strong>
        <small class="text-muted ms-2" *ngIf="!readonly && isSeparateEvaluation && activeEvaluationTab === 'Technical' && technicalWeightage === 100">
            (Must equal exactly 100%)
        </small>
        <small class="text-muted ms-2" *ngIf="!readonly && isSeparateEvaluation && activeEvaluationTab === 'Commercial' && commercialWeightage === 100">
            (Must equal exactly 100%)
        </small>
        <small class="text-muted ms-2" *ngIf="!readonly && isJointEvaluation">
            (Recommended: 100%)
        </small>
        <small class="text-muted ms-2" *ngIf="!readonly && isSeparateEvaluation && ((activeEvaluationTab === 'Technical' && technicalWeightage !== 100) || (activeEvaluationTab === 'Commercial' && commercialWeightage !== 100))">
            (Maximum: 100%)
        </small>
    </div>

    <!-- Criteria Table with Tabs - Show in both edit and readonly mode -->
    <div class="table-responsive">
        <table class="table table-bordered">
            <thead>
                <tr>
                    <th>
                        {{ activeEvaluationTab }} Evaluation Criteria
                        <span class="text-danger" *ngIf="!readonly">*</span>
                    </th>
                    <th style="width: 200px;">Weightage</th>
                    <th style="width: 100px;" *ngIf="!readonly">Action</th>
                </tr>
            </thead>
            <tbody [formArrayName]="activeEvaluationTab === 'Technical' ? 'technicalEvaluationCriteria' : 'commercialEvaluationCriteria'">
                <!-- Show from form array if available -->
                <ng-container *ngIf="currentEvaluationCriteriaFormArray.length > 0">
                    <tr 
                        *ngFor="let criteria of currentEvaluationCriteriaFormArray.controls; let i = index; trackBy: trackByCriteriaControl" 
                        [formGroupName]="i">
                        <td>
                            <input 
                                *ngIf="!readonly"
                                type="text" 
                                class="form-control rounded-4 bg-light p-2" 
                                [class.border-danger]="criteria.get('description')?.invalid && criteria.get('description')?.touched"
                                formControlName="description" 
                                placeholder="Enter {{ activeEvaluationTab.toLowerCase() }} evaluation criteria"
                                required>
                            <span *ngIf="readonly" class="form-control-plaintext">
                                {{ criteria.get('description')?.value || '' }}
                            </span>
                            <small class="text-danger" *ngIf="!readonly && criteria.get('description')?.invalid && criteria.get('description')?.touched">
                                Description is required
                            </small>
                        </td>
                        <td>
                            <input 
                                *ngIf="!readonly"
                                type="number" 
                                class="form-control rounded-4 p-2" 
                                [class.border-danger]="isCurrentCriteriaError()"
                                [class.border-warning]="isCurrentCriteriaWarning()"
                                formControlName="weightage" 
                                placeholder="0.000000"
                                step="0.000001"
                                min="0"
                                (input)="validateCriteriaWeightages()">
                            <span *ngIf="readonly" class="form-control-plaintext">
                                {{ (criteria.get('weightage')?.value || 0) | number: '1.2-2' }}%
                            </span>
                        </td>
                        <td class="text-center" *ngIf="!readonly">
                            <button 
                                type="button" 
                                class="btn action-btn btn-sm"
                                (click)="removeEvaluationCriteria(i)"
                                title="Delete">
                                <i class="fas fa-trash"></i> Delete
                            </button>
                        </td>
                    </tr>
                </ng-container>
                <!-- Fallback: Show from initialData if form array is empty (for readonly mode) -->
                <ng-container *ngIf="readonly && currentEvaluationCriteriaFormArray.length === 0">
                    <ng-container *ngIf="activeEvaluationTab === 'Technical' && initialData && initialData.technicalEvaluationCriteria && initialData.technicalEvaluationCriteria.length > 0">
                        <tr *ngFor="let criteria of initialData.technicalEvaluationCriteria; let i = index">
                            <td>{{ criteria.description || '' }}</td>
                            <td>{{ (criteria.weightage || 0) | number: '1.2-2' }}%</td>
                        </tr>
                    </ng-container>
                    <ng-container *ngIf="activeEvaluationTab === 'Commercial' && initialData && initialData.commercialEvaluationCriteria && initialData.commercialEvaluationCriteria.length > 0">
                        <tr *ngFor="let criteria of initialData.commercialEvaluationCriteria; let i = index">
                            <td>{{ criteria.description || '' }}</td>
                            <td>{{ (criteria.weightage || 0) | number: '1.2-2' }}%</td>
                        </tr>
                    </ng-container>
                </ng-container>
                <!-- Empty state -->
                <tr *ngIf="currentEvaluationCriteriaFormArray.length === 0 && (!readonly || (readonly && ((activeEvaluationTab === 'Technical' && (!initialData || !initialData.technicalEvaluationCriteria || initialData.technicalEvaluationCriteria.length === 0)) || (activeEvaluationTab === 'Commercial' && (!initialData || !initialData.commercialEvaluationCriteria || initialData.commercialEvaluationCriteria.length === 0)))))">
                    <td [attr.colspan]="readonly ? 2 : 3" class="text-center text-muted py-4">
                        <span *ngIf="!readonly">No {{ activeEvaluationTab.toLowerCase() }} evaluation criteria added yet. Click "+ New" to add criteria.</span>
                        <span *ngIf="readonly">No {{ activeEvaluationTab.toLowerCase() }} evaluation criteria available.</span>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</div>
